import { defineStore } from 'pinia'

import { getMember as getMemberService, postMemberRegionChange } from '@/service/member'
import { logout as serviceLogout } from '@/service/login'

import type { AuthStep, PendingThirdPartyAuth } from '@/types/auth'
import type { EatigoMemberInfoData } from '@/types/api'
import { EdbMemberAuthType } from '@/types/api'
import { Gender } from '@/types/member'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<Nullable<EatigoMemberInfoData>>(null)
  const isAlreadyGetMember = ref(false)
  const loginPendingActions = ref<(() => void)[]>([])
  const pendingThirdPartyAuth = ref<Nullable<PendingThirdPartyAuth>>(null)

  const isLogin = computed(() => !!user.value?.account || !!user.value?.email)

  const username = computed(() => {
    if (user.value?.first_name) {
      return user.value.first_name
    }

    switch (user.value?.auth_type) {
      case EdbMemberAuthType.MemberAuthTypeFB:
      case EdbMemberAuthType.MemberAuthTypeGoogle:
      case EdbMemberAuthType.MemberAuthTypeEmail:
      default:
        return user.value?.email?.split('@')[0] ?? ''
    }
  })

  const getMember = async () => {
    try {
      const resp = await getMemberService()

      if (resp?.code !== 0) {
        throw resp
      }
      const gender = resp.data.gender
      user.value = { ...resp.data, gender: gender === Gender.NOT_SPECIFIED ? Gender.OTHER : gender }
      return resp
    } catch (e) {
      if (import.meta.server) return
      console.error(e)
    }
  }

  const updateMemberRegion = async (regionId: number) => {
    try {
      await postMemberRegionChange(regionId)
      await getMember()
    } catch (e) {
      if (import.meta.server) return
      console.error(e)
    }
  }

  const logout = async (clear = true) => {
    try {
      await serviceLogout()
      resetDeviceId()
      if (clear) {
        user.value = null
      }
    } catch (err) {
      if (err instanceof ErrorEvent) {
        console.error(err.message)
      }
    }
  }

  // Modal
  const isLoginModalShown = ref(false)
  const authStep = ref<AuthStep>('login-menu')

  // 這裡的 authLayer 是用來控制 login modal 內部的不同層級（主層、確認層、第三方同意層）
  const authLayer = ref<'main' | 'confirm-discard' | 'third-party-consent'>('main')

  const previousAuthStep = ref<Nullable<AuthStep>>(null)

  const openLoginModal = () => {
    isLoginModalShown.value = true
  }

  /**
   * 嘗試關閉登入 Modal，根據當前 authLayer 決定是否需要顯示確認彈窗
   * @param shouldConfirmClose - 是否需要確認關閉（主要用於註冊表單有填寫內容的情況）
   * @returns 是否阻止關閉（true: 阻止，false: 允許）
   *
   **/
  const tryCloseLoginModal = (shouldConfirmClose: boolean = false): boolean => {
    if (authStep.value === 'sign-up' && shouldConfirmClose) {
      previousAuthStep.value = 'sign-up'
      authLayer.value = 'confirm-discard'
      return true
    }

    if (authStep.value === 'third-party-consent') {
      previousAuthStep.value = 'third-party-consent'
      authLayer.value = 'confirm-discard'
      return true
    }

    return false
  }

  const handleConfirmClose = (action: 'back' | 'cancel') => {
    if (action !== 'back') {
      previousAuthStep.value = null
      closeLoginModal()
      return
    }

    if (previousAuthStep.value) {
      changeAuthStep(previousAuthStep.value)
      previousAuthStep.value = null
    }
  }

  const cancelConfirmClose = () => {
    authLayer.value = 'main'
  }

  const resetLoginModal = () => {
    authLayer.value = 'main'
    previousAuthStep.value = null
    authStep.value = 'login-menu'
    pendingThirdPartyAuth.value = null
  }

  const closeLoginModal = () => {
    isLoginModalShown.value = false
  }

  const changeAuthStep = (value: AuthStep) => {
    authStep.value = value

    authLayer.value = value === 'third-party-consent' ? 'third-party-consent' : 'main'
  }

  const setPendingThirdPartyAuth = (auth: PendingThirdPartyAuth) => {
    pendingThirdPartyAuth.value = auth
  }

  const addToLoginPendingActions = (task: () => void) => {
    loginPendingActions.value.push(task)
  }

  return {
    user,
    isLogin,
    isAlreadyGetMember,
    username,

    getMember,
    updateMemberRegion,
    logout,

    isLoginModalShown,
    authStep,
    authLayer,
    openLoginModal,
    closeLoginModal,
    resetLoginModal,
    tryCloseLoginModal,
    changeAuthStep,

    // 確認層控制
    handleConfirmClose,
    cancelConfirmClose,
    previousAuthStep,

    // 第三方登入暫存
    pendingThirdPartyAuth,
    setPendingThirdPartyAuth,

    loginPendingActions,
    addToLoginPendingActions,
  }
})
